import {hideElem, showElem, toggleElem} from '../utils/dom.ts';
import {GET} from '../modules/fetch.ts';
import {urlQueryEscape} from '../utils/url.ts';
import {sanitizeRepoName} from './repo-common.ts';

const {appSubUrl} = window.config;

const service = document.querySelector<HTMLInputElement>('#service_type');
const user = document.querySelector<HTMLInputElement>('#auth_username');
const pass = document.querySelector<HTMLInputElement>('#auth_password');
const token = document.querySelector<HTMLInputElement>('#auth_token');
const mirror = document.querySelector<HTMLInputElement>('#mirror');
const lfs = document.querySelector<HTMLInputElement>('#lfs');
const lfsSettings = document.querySelector<HTMLElement>('#lfs_settings')!;
const lfsEndpoint = document.querySelector<HTMLElement>('#lfs_endpoint')!;
const items = document.querySelectorAll<HTMLInputElement>('#migrate_items input[type=checkbox]');

type GitHubHit = {name: string, hint?: string};

function initGitHubBatchPicker() {
  const ownerEl = document.querySelector<HTMLSelectElement>('#github_owner');
  const cloneEl = document.querySelector<HTMLInputElement>('#clone_addr');
  const nameEl = document.querySelector<HTMLInputElement>('#repo_name');
  const checklist = document.querySelector<HTMLElement>('#github_repo_checklist');
  const filterEl = document.querySelector<HTMLInputElement>('#github_repo_filter');
  const allEl = document.querySelector<HTMLInputElement>('#github_repo_all');
  const btn = document.querySelector<HTMLButtonElement>('#github_migrate_btn');
  const singleDest = document.querySelector<HTMLElement>('#github_single_dest');
  const backup = document.querySelector<HTMLDetailsElement>('.github-url-backup');
  if (!ownerEl || !checklist) return;

  let repos: GitHubHit[] = [];
  let repoNameChanged = false;
  nameEl?.addEventListener('input', () => {repoNameChanged = true});

  function selectedBoxes(): HTMLInputElement[] {
    return [...checklist.querySelectorAll<HTMLInputElement>('input[name="github_repos"]:checked')];
  }

  function updateButton() {
    if (!btn) return;
    const n = selectedBoxes().length;
    const url = (cloneEl?.value ?? '').trim();
    const usingUrl = Boolean(backup?.open && url);
    if (usingUrl) {
      btn.textContent = btn.dataset.labelOne ?? 'Migrate Repository';
      btn.disabled = false;
      singleDest?.classList.remove('tw-hidden');
      if (nameEl && !repoNameChanged) {
        let fromUrl = url.split(/[?#]/)[0];
        const parts = /^(.*\/)?((.+?)\/?)$/.exec(fromUrl);
        if (parts && parts.length >= 4) {
          nameEl.value = sanitizeRepoName(parts[3].split(/[?#]/)[0]);
        }
      }
      return;
    }
    singleDest?.classList.add('tw-hidden');
    if (n <= 1) {
      btn.textContent = btn.dataset.labelOne ?? 'Migrate Repository';
    } else {
      btn.textContent = (btn.dataset.labelN ?? 'Migrate %d repositories').replace('%d', String(n));
    }
    btn.disabled = n === 0;
  }

  const privateLabel = checklist.dataset.privateLabel ?? 'Private';

  function renderChecklist() {
    const q = (filterEl?.value ?? '').trim().toLowerCase();
    const owner = ownerEl.value.trim();
    checklist.replaceChildren();
    const visible = repos.filter((r) => !q || r.name.toLowerCase().includes(q));
    if (!visible.length) {
      const empty = document.createElement('p');
      empty.className = 'help';
      empty.textContent = checklist.dataset.empty ?? 'No repositories for this owner.';
      checklist.append(empty);
      if (allEl) allEl.disabled = true;
      updateButton();
      return;
    }
    if (allEl) allEl.disabled = false;
    for (const repo of visible) {
      const fullName = `${owner}/${repo.name}`;
      const sourcePrivate = (repo.hint ?? '').toLowerCase() === 'private';
      const row = document.createElement('div');
      row.className = 'github-repo-row';

      const pick = document.createElement('label');
      pick.className = 'github-repo-pick';
      const box = document.createElement('input');
      box.type = 'checkbox';
      box.name = 'github_repos';
      box.value = fullName;
      box.addEventListener('change', updateButton);
      const name = document.createElement('span');
      name.className = 'name';
      name.textContent = repo.name;
      pick.append(box, name);
      row.append(pick);

      if (repo.hint) {
        const hint = document.createElement('span');
        hint.className = 'hint';
        hint.textContent = repo.hint;
        row.append(hint);
      }

      const priv = document.createElement('label');
      priv.className = 'github-repo-private';
      const privBox = document.createElement('input');
      privBox.type = 'checkbox';
      privBox.name = 'github_private';
      privBox.value = fullName;
      privBox.checked = sourcePrivate;
      const privText = document.createElement('span');
      privText.textContent = privateLabel;
      priv.append(privBox, privText);
      row.append(priv);

      checklist.append(row);
    }
    updateButton();
  }

  async function loadRepos(owner: string) {
    repos = [];
    checklist.replaceChildren();
    const loading = document.createElement('p');
    loading.className = 'help';
    loading.textContent = checklist.dataset.loading ?? 'Loading…';
    checklist.append(loading);
    if (filterEl) filterEl.disabled = true;
    if (allEl) {
      allEl.checked = false;
      allEl.disabled = true;
    }
    if (!owner) {
      checklist.replaceChildren();
      updateButton();
      return;
    }
    try {
      const res = await GET(`${appSubUrl}/repo/migrate/github/repos?owner=${urlQueryEscape(owner)}`, {credentials: 'same-origin'});
      if (!res.ok) {
        repos = [];
        renderChecklist();
        return;
      }
      const data = await res.json() as {items?: GitHubHit[]};
      repos = data.items ?? [];
    } catch {
      repos = [];
    }
    if (filterEl) filterEl.disabled = false;
    renderChecklist();
  }

  async function loadOwners() {
    try {
      const res = await GET(`${appSubUrl}/repo/migrate/github/owners`, {credentials: 'same-origin'});
      if (!res.ok) {
        ownerEl.replaceChildren();
        const opt = document.createElement('option');
        opt.value = '';
        opt.textContent = btn?.dataset.emptyOwner ?? 'Could not load GitHub owners. Reconnect GitHub.';
        ownerEl.append(opt);
        return;
      }
      const data = await res.json() as {items?: GitHubHit[]};
      const items = data.items ?? [];
      ownerEl.replaceChildren();
      if (!items.length) {
        const opt = document.createElement('option');
        opt.value = '';
        opt.textContent = btn?.dataset.emptyOwner ?? 'Could not load GitHub owners. Reconnect GitHub.';
        ownerEl.append(opt);
        return;
      }
      for (const item of items) {
        const opt = document.createElement('option');
        opt.value = item.name;
        opt.textContent = item.hint ? `${item.name} (${item.hint})` : item.name;
        ownerEl.append(opt);
      }
      await loadRepos(ownerEl.value);
    } catch {
      ownerEl.replaceChildren();
      const opt = document.createElement('option');
      opt.value = '';
      opt.textContent = btn?.dataset.emptyOwner ?? 'Could not load GitHub owners. Reconnect GitHub.';
      ownerEl.append(opt);
    }
  }

  ownerEl.addEventListener('change', () => {void loadRepos(ownerEl.value)});
  filterEl?.addEventListener('input', renderChecklist);
  allEl?.addEventListener('change', () => {
    const on = Boolean(allEl.checked);
    for (const box of checklist.querySelectorAll<HTMLInputElement>('input[name="github_repos"]')) {
      box.checked = on;
    }
    updateButton();
  });
  backup?.addEventListener('toggle', updateButton);
  cloneEl?.addEventListener('input', updateButton);

  void loadOwners();
  updateButton();
}

export function initRepoMigration() {
  checkAuth();
  setLFSSettingsVisibility();

  user?.addEventListener('input', () => {checkItems(false)});
  pass?.addEventListener('input', () => {checkItems(false)});
  token?.addEventListener('input', () => {checkItems(true)});
  mirror?.addEventListener('change', () => {checkItems(true)});
  document.querySelector('#lfs_settings_show')?.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    showElem(lfsEndpoint);
  });
  lfs?.addEventListener('change', setLFSSettingsVisibility);

  initGitHubBatchPicker();

  const elCloneAddr = document.querySelector<HTMLInputElement>('#clone_addr');
  const elRepoName = document.querySelector<HTMLInputElement>('#repo_name');
  if (elCloneAddr && elRepoName && !document.querySelector('#github_owner')) {
    let repoNameChanged = false;
    elRepoName.addEventListener('input', () => {repoNameChanged = true});
    elCloneAddr.addEventListener('input', () => {
      if (repoNameChanged) return;
      let repoNameFromUrl = elCloneAddr.value.split(/[?#]/)[0];
      const parts = /^(.*\/)?((.+?)\/?)$/.exec(repoNameFromUrl);
      if (!parts || parts.length < 4) {
        elRepoName.value = '';
        return;
      }
      repoNameFromUrl = parts[3].split(/[?#]/)[0];
      elRepoName.value = sanitizeRepoName(repoNameFromUrl);
    });
  }
}

function checkAuth() {
  if (!service) return;
  const serviceType = Number(service.value);

  checkItems(serviceType !== 1);
}

function checkItems(tokenAuth: boolean) {
  let enableItems: boolean;
  const githubConnected = Boolean(document.querySelector('#github_connected'));
  if (githubConnected) {
    enableItems = true;
  } else if (tokenAuth) {
    enableItems = token?.value !== '';
  } else {
    enableItems = user?.value !== '' || pass?.value !== '';
  }
  if (enableItems && Number(service?.value) > 1) {
    if (mirror?.checked) {
      for (const item of items) {
        item.disabled = item.name !== 'wiki';
      }
      return;
    }
    for (const item of items) item.disabled = false;
  } else {
    for (const item of items) item.disabled = true;
  }
}

function setLFSSettingsVisibility() {
  if (!lfs) return;
  const visible = lfs.checked;
  toggleElem(lfsSettings, visible);
  hideElem(lfsEndpoint);
}
